import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useRole } from '@/contexts/RoleContext';
import { MainLayout } from '@/layouts/MainLayout';
import { notesApi, timelineApi, documentVerificationApi, userProfileApi, nudgeApi, loansApi } from '@/api';
import type { Loan } from '@/types/loans.types';
import type { LoanNote } from '@/types/notes.types';
import type { TimelineEvent } from '@/types/timeline.types';
import type { KYCDocument } from '@/types/documentVerification.types';
import type { NudgeDayGroup, NudgeEvent } from '@/types/nudge.types';
import { Skeleton } from '@/components/common';
import styles from './LoanDetail.module.css';

type TabType = 'overview' | 'notes' | 'documents' | 'timeline' | 'analysis';

export const LoanDetail: React.FC = () => {
    const { loanId } = useParams<{ loanId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const { selectedRole } = useRole();
    const [activeTab, setActiveTab] = useState<TabType>('overview');

    // Get loan from route state (passed from Loans page)
    const loanFromState = location.state?.loan as Loan | undefined;
    const [loading, setLoading] = useState(!loanFromState);
    const [error, setError] = useState('');

    const [loan, setLoan] = useState<Loan | null>(loanFromState || null);

    // Check if Documents tab should be shown
    const shouldShowDocumentsTab = useMemo(() => {
        if (!loan) return false;

        // Show if role is DOCUMENT_VERIFIER
        if (selectedRole?.role_type === 'DOCUMENT_VERIFIER') {
            return true;
        }

        // Show if role is agent (null) and clickpe_page_id is cp_page_9
        if (selectedRole === null && loan.clickpe_page_id === 'cp_page_9') {
            return true;
        }

        return false;
    }, [loan, selectedRole]);

    // Notes
    const [notes, setNotes] = useState<LoanNote[]>([]);
    const [newNote, setNewNote] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [loadingNotes, setLoadingNotes] = useState(false);

    // Timeline
    const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
    const [loadingTimeline, setLoadingTimeline] = useState(false);

    // Documents
    const [kycDocuments, setKycDocuments] = useState<KYCDocument[]>([]);
    const [analysisData, setAnalysisData] = useState<Record<string, unknown> | null>(null);
    const [analysisStatus, setAnalysisStatus] = useState<'idle' | 'loading' | 'syncing' | 'success' | 'error'>('idle');
    const [loadingDocuments, setLoadingDocuments] = useState(false);

    // Actions Taken
    const [actionsTaken, setActionsTaken] = useState<NudgeDayGroup[]>([]);
    const [loadingActions, setLoadingActions] = useState(false);
    const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/agent-login');
        }
    }, [isAuthenticated, navigate]);

    const fetchLoanById = async (id: string) => {
        setLoading(true);
        setError('');
        try {
            const agentId = localStorage.getItem('agent_id') || localStorage.getItem('agentId') || '';
            const response = await loansApi.getLoanDetails({
                query_type: 'new_get_agent_loans',
                agent_id: agentId,
                filters: { loan_id: id },
                page: 1,
                page_size: 1,
            });
            if (response.response && response.response.length > 0) {
                setLoan(response.response[0]);
            } else {
                setError('Loan not found.');
            }
        } catch (err) {
            console.error('Failed to fetch loan details:', err);
            setError('Failed to fetch loan details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (loanFromState) {
            setLoan(loanFromState);
            setLoading(false);
        } else if (loanId) {
            // No state passed (e.g. direct navigation or from Voice Calls) — fetch from API
            fetchLoanById(loanId);
        }
    }, [loanId, loanFromState]);

    // Switch to overview tab if documents tab becomes unavailable
    useEffect(() => {
        if (activeTab === 'documents' && !shouldShowDocumentsTab) {
            setActiveTab('overview');
        }
    }, [shouldShowDocumentsTab, activeTab]);

    useEffect(() => {
        if (loan && activeTab === 'notes') {
            fetchNotes();
        }
        if (loan && activeTab === 'timeline') {
            fetchTimeline();
        }
        if (loan && activeTab === 'documents' && shouldShowDocumentsTab) {
            fetchDocuments();
            fetchAnalysisData();
        }
        if (loan && activeTab === 'analysis') {
            fetchAnalysisData();
        }
    }, [loan, activeTab, shouldShowDocumentsTab]);

    // Fetch actions taken when loan is available
    useEffect(() => {
        if (loan?.loan_id) {
            fetchActionsTaken();
        }
    }, [loan?.loan_id]);


    const fetchNotes = async () => {
        if (!loanId) return;

        setLoadingNotes(true);
        try {
            const response = await notesApi.getLoanNotes({ loan_id: loanId });
            if (response.status === 'Success' && response.data) {
                setNotes(response.data.notes || []);
            }
        } catch (err) {
            console.error('Failed to fetch notes:', err);
        } finally {
            setLoadingNotes(false);
        }
    };

    const fetchTimeline = async () => {
        if (!loan?.user_id) return;

        setLoadingTimeline(true);
        try {
            const response = await timelineApi.getUserTimeline({
                query_name: 'cp_mfl_user_timeline',
                key: { user_id: loan.user_id },
            });
            if (response.status === 'Success' && response.response) {
                setTimeline(response.response.timeline || []);
            }
        } catch (err) {
            console.error('Failed to fetch timeline:', err);
        } finally {
            setLoadingTimeline(false);
        }
    };

    const fetchDocuments = async () => {
        if (!loan?.user_id) return;

        setLoadingDocuments(true);
        try {
            const response = await documentVerificationApi.getKYCDocuments({ user_id: loan.user_id });
            if (response.status && response.data) {
                setKycDocuments(response.data.documents || []);
            }
        } catch (err) {
            console.error('Failed to fetch documents:', err);
        } finally {
            setLoadingDocuments(false);
        }
    };

    const fetchActionsTaken = async () => {
        if (!loanId) return;

        setLoadingActions(true);
        try {
            const response = await nudgeApi.getActionsTaken({ loan_id: loanId });
            if (response.status === 'Success' && response.response) {
                setActionsTaken(response.response.timeline || []);
            }
        } catch (err) {
            console.error('Failed to fetch actions taken:', err);
        } finally {
            setLoadingActions(false);
        }
    };

    const fetchAnalysisData = async () => {
        if (!loan?.user_id) return;

        try {
            const response = await documentVerificationApi.getDocumentAnalysisData({
                user_id: loan.user_id,
                query_type: 'get',
            });
            if (response.status && response.data) {
                setAnalysisData(response.data.extracted_fields || null);
            }
        } catch (err) {
            console.error('Failed to fetch analysis data:', err);
        }
    };

    const handleAddNote = async () => {
        if (!loanId || !newNote.trim()) return;

        setAddingNote(true);
        try {
            const agentName = loan?.agent_name || 'Agent';
            const response = await notesApi.addLoanNote({
                loan_id: loanId,
                createdAt: new Date().toISOString(),
                createdBy: agentName,
                note: `<p>${newNote.replace(/\n/g, '<br>')}</p>`,
                userGroup: 'CP',
                tag: 'cp_mfl',
            });

            if (response.status) {
                setNewNote('');
                await fetchNotes();
            } else {
                setError(response.message || 'Failed to add note');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add note');
        } finally {
            setAddingNote(false);
        }
    };

    const handleSyncAnalysis = async () => {
        if (!loan?.user_id) return;

        setAnalysisStatus('syncing');
        try {
            // Request analysis
            const requestResponse = await documentVerificationApi.requestDocumentAnalysis({
                user_id: loan.user_id,
            });

            if (requestResponse.status && requestResponse.data) {
                const runId = requestResponse.data.run_id;

                // Poll for completion
                let pollCount = 0;
                const maxPolls = 15; // 30 seconds max (15 * 2 seconds)

                const pollInterval = setInterval(async () => {
                    pollCount++;
                    try {
                        const statusResponse = await documentVerificationApi.requestDocumentAnalysis({
                            user_id: loan.user_id,
                            run_id: runId,
                        });

                        if (statusResponse.data.status === 'SUCCESS' || statusResponse.data.status === 'COMPLETED') {
                            clearInterval(pollInterval);
                            setAnalysisStatus('success');
                            await fetchAnalysisData();
                        } else if (statusResponse.data.status === 'FAILED') {
                            clearInterval(pollInterval);
                            setAnalysisStatus('error');
                        } else if (pollCount >= maxPolls) {
                            clearInterval(pollInterval);
                            setAnalysisStatus('error');
                        }
                    } catch (err) {
                        clearInterval(pollInterval);
                        setAnalysisStatus('error');
                    }
                }, 2000);
            } else {
                setAnalysisStatus('error');
            }
        } catch (err) {
            setAnalysisStatus('error');
        }
    };

    if (!isAuthenticated) {
        return null;
    }

    if (loading) {
        return (
            <MainLayout>
                <div className={styles.container}>
                    <div className={styles.loadingSkeleton}>
                        <Skeleton className={styles.skeletonHeader} />
                        <div className={styles.skeletonContent}>
                            <Skeleton className={styles.skeletonLeftPanel} />
                            <Skeleton className={styles.skeletonRightPanel} />
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    if (error && !loan) {
        return (
            <MainLayout>
                <div className={styles.container}>
                    <div className={styles.error}>{error}</div>
                </div>
            </MainLayout>
        );
    }

    if (!loan) {
        return null;
    }

    const userName = [loan.fname, loan.mname, loan.lname].filter(Boolean).join(' ') || 'N/A';
    const userInitials = userName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2) || 'NA';

    const getStatusColor = (status?: string) => {
        if (!status) return '#6b7280';
        const statusUpper = status.toUpperCase();
        if (statusUpper.includes('ACTIVE') || statusUpper.includes('SUCCESS')) return '#10b981';
        if (statusUpper.includes('REJECTED') || statusUpper.includes('FAILED')) return '#ef4444';
        if (statusUpper.includes('PENDING') || statusUpper.includes('REVIEW')) return '#f59e0b';
        return '#6b7280';
    };

    const getEventTypeLabel = (eventType: string) => {
        switch (eventType) {
            case 'whatsapp': return 'WhatsApp';
            case 'ai_call': return 'AI Call';
            case 'agent_call': return 'Agent Call';
            default: return eventType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
        }
    };

    const getEventTypeIcon = (eventType: string) => {
        switch (eventType) {
            case 'whatsapp': return '💬';
            case 'ai_call': return '📞';
            case 'agent_call': return '🎧';
            default: return '📋';
        }
    };

    const getEventCardClass = (eventType: string) => {
        switch (eventType) {
            case 'whatsapp': return styles.actionsEventCardWhatsapp;
            case 'ai_call': return styles.actionsEventCardAiCall;
            case 'agent_call': return styles.actionsEventCardAgentCall;
            default: return styles.actionsEventCardDefault;
        }
    };

    const getStatusClass = (status: string) => {
        switch (status) {
            case 'initiated': return styles.actionsStatusInitiated;
            case 'executed': return styles.actionsStatusExecuted;
            case 'failed': return styles.actionsStatusFailed;
            default: return styles.actionsStatusDefault;
        }
    };

    const getInterestClass = (percentage: number) => {
        if (percentage >= 70) return styles.actionsInterestHigh;
        if (percentage >= 40) return styles.actionsInterestMedium;
        return styles.actionsInterestLow;
    };

    const formatEventTime = (dateStr: string) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
        } catch {
            return dateStr;
        }
    };

    const formatDayGroupDate = (events: NudgeEvent[]) => {
        if (events.length === 0) return '';
        try {
            const dateStr = events[0].execute_at || events[0].created_at;
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        } catch {
            return '';
        }
    };

    const formatDuration = (seconds: number) => {
        if (seconds < 60) return `${Math.round(seconds)}s`;
        const mins = Math.floor(seconds / 60);
        const secs = Math.round(seconds % 60);
        return `${mins}m ${secs}s`;
    };

    const toggleEventExpand = (eventId: string) => {
        setExpandedEventId((prev) => (prev === eventId ? null : eventId));
    };

    const renderEventDetails = (event: NudgeEvent) => {
        if (event.event_type === 'whatsapp' && event.whatsapp_details) {
            const { is_delivered, is_read } = event.whatsapp_details;
            return (
                <div className={styles.actionsEventDetails}>
                    <div className={styles.actionsDetailRow}>
                        <span className={styles.actionsDetailIcon}>{is_delivered ? '✓' : '○'}</span>
                        <span>Delivered{is_delivered ? ` · ${formatEventTime(is_delivered)}` : ''}</span>
                    </div>
                    <div className={styles.actionsDetailRow}>
                        <span className={styles.actionsDetailIcon}>{is_read ? '✓✓' : '○'}</span>
                        <span>Read{is_read ? ` · ${formatEventTime(is_read)}` : ''}</span>
                    </div>
                </div>
            );
        }

        if (event.event_type === 'ai_call' && event.call_details) {
            const { duration, interest_percentage, qualified, disqualification_reason, call_analysis, qualification_reasoning } = event.call_details;
            const isExpanded = expandedEventId === event.event_id;

            return (
                <div className={styles.actionsEventDetails}>
                    <div className={styles.actionsDetailRow}>
                        <span className={styles.actionsCallDuration}>{formatDuration(duration)}</span>
                        <span className={`${styles.actionsInterestBadge} ${getInterestClass(interest_percentage)}`}>
                            {interest_percentage}% interest
                        </span>
                    </div>
                    <div className={styles.actionsDetailRow}>
                        <span className={`${styles.actionsQualifiedBadge} ${qualified ? styles.actionsQualifiedYes : styles.actionsQualifiedNo}`}>
                            {qualified ? 'Qualified' : 'Not Qualified'}
                        </span>
                        {!qualified && disqualification_reason && (
                            <span className={styles.actionsDisqualReason}>
                                {disqualification_reason.replace(/_/g, ' ')}
                            </span>
                        )}
                    </div>
                    {isExpanded && (
                        <div className={styles.actionsExpandedDetails}>
                            {call_analysis && call_analysis.length > 0 && (
                                <div className={styles.actionsCallAnalysis}>
                                    {call_analysis.map((item, i) => (
                                        <span key={i} className={styles.actionsCallAnalysisItem}>{item}</span>
                                    ))}
                                </div>
                            )}
                            {qualification_reasoning && (
                                <div className={styles.actionsQualReasoning}>{qualification_reasoning}</div>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        if (event.event_type === 'agent_call' && event.agent_call_details) {
            const details = event.agent_call_details;
            const summary = details.call_summary;
            const durationSec = parseInt(details.duration, 10) || 0;
            const isExpanded = expandedEventId === event.event_id;

            const getCallStatusClass = (status: string) => {
                switch (status) {
                    case 'answered': return styles.actionsQualifiedYes;
                    case 'missed': case 'no-answer': return styles.actionsQualifiedNo;
                    default: return '';
                }
            };

            const getOutcomeClass = (outcome?: string) => {
                if (!outcome) return '';
                const lower = outcome.toLowerCase();
                if (lower.includes('interested') && !lower.includes('not')) return styles.actionsInterestHigh;
                if (lower.includes('not interested')) return styles.actionsInterestLow;
                return styles.actionsInterestMedium;
            };

            return (
                <div className={styles.actionsEventDetails}>
                    <div className={styles.actionsDetailRow}>
                        <span className={styles.actionsCallDuration}>{formatDuration(durationSec)}</span>
                        <span className={`${styles.actionsQualifiedBadge} ${getCallStatusClass(details.call_status)}`}>
                            {details.call_status}
                        </span>
                        <span className={styles.actionsDisqualReason}>
                            {details.call_direction}
                        </span>
                    </div>
                    {summary?.call_outcome && (
                        <div className={styles.actionsDetailRow}>
                            <span className={`${styles.actionsInterestBadge} ${getOutcomeClass(summary.call_outcome)}`}>
                                {summary.call_outcome}
                            </span>
                            {summary.agent_quality_score != null && (
                                <span className={styles.actionsAgentScoreBadge}>
                                    Score: {summary.agent_quality_score}/10
                                </span>
                            )}
                        </div>
                    )}
                    {isExpanded && summary && (
                        <div className={styles.actionsExpandedDetails}>
                            {summary.call_summary && (
                                <div className={styles.actionsQualReasoning}>
                                    {summary.call_summary}
                                </div>
                            )}
                            {summary.agent_strengths && summary.agent_strengths.length > 0 && (
                                <div className={styles.actionsAgentSection}>
                                    <span className={styles.actionsAgentSectionTitle}>Strengths</span>
                                    <div className={styles.actionsCallAnalysis}>
                                        {summary.agent_strengths.map((item, i) => (
                                            <span key={i} className={styles.actionsCallAnalysisItem}>{item}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {summary.improvement_areas && summary.improvement_areas.length > 0 && (
                                <div className={styles.actionsAgentSection}>
                                    <span className={styles.actionsAgentSectionTitle}>Areas to Improve</span>
                                    <div className={styles.actionsCallAnalysis}>
                                        {summary.improvement_areas.map((item, i) => (
                                            <span key={i} className={styles.actionsCallAnalysisItem}>{item}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {summary.objections_raised && summary.objections_raised.length > 0 && (
                                <div className={styles.actionsAgentSection}>
                                    <span className={styles.actionsAgentSectionTitle}>Objections Raised</span>
                                    <div className={styles.actionsCallAnalysis}>
                                        {summary.objections_raised.map((item, i) => (
                                            <span key={i} className={styles.actionsCallAnalysisItem}>{item}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className={styles.actionsAgentMetaRow}>
                                {summary.customer_sentiment && (
                                    <span className={styles.actionsAgentMetaTag}>Sentiment: {summary.customer_sentiment}</span>
                                )}
                                {summary.conversion_likelihood && (
                                    <span className={styles.actionsAgentMetaTag}>Conversion: {summary.conversion_likelihood}</span>
                                )}
                                {summary.agent_confidence_level && (
                                    <span className={styles.actionsAgentMetaTag}>Confidence: {summary.agent_confidence_level}</span>
                                )}
                            </div>
                            {details.recording_url && (
                                <div className={styles.actionsRecordingSection}>
                                    <audio controls className={styles.actionsRecordingPlayer}>
                                        <source src={details.recording_url} />
                                    </audio>
                                </div>
                            )}
                            {summary.transcript && (
                                <div className={styles.actionsTranscriptSection}>
                                    <span className={styles.actionsAgentSectionTitle}>Transcript</span>
                                    <div className={styles.actionsTranscriptText}>{summary.transcript}</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        return null;
    };

    return (
        <MainLayout>
            <div className={styles.container}>
                <button
                    className={styles.backButton}
                    onClick={() => navigate(-1)}
                    title="Back to Loans"
                >
                    ← Back to Loans
                </button>

                {error && <div className={styles.errorMessage}>{error}</div>}

                <div className={styles.mainLayout}>
                    {/* Left Panel - Applicant Information */}
                    <div className={styles.leftPanel}>
                        <div className={styles.applicantCard}>
                            <div className={styles.profileSection}>
                                <div className={styles.avatar}>{userInitials}</div>
                                <div className={styles.applicantLabel}>ADDITIONAL_DOCUMENTS-POST_OFFER</div>
                                <h2 className={styles.applicantName}>{userName}</h2>
                            </div>

                            <div className={styles.contactInfo}>
                                {loan.mob_num && (
                                    <div className={styles.contactItem}>
                                        <span className={styles.contactIcon}>📞</span>
                                        <span>{loan.mob_num}</span>
                                    </div>
                                )}
                                {loan.email && (
                                    <div className={styles.contactItem}>
                                        <span className={styles.contactIcon}>✉️</span>
                                        <span>{loan.email}</span>
                                    </div>
                                )}
                            </div>

                            {/* Actions Section */}
                            <div style={{
                                padding: '16px',
                                borderTop: '1px solid #e5e7eb',
                                borderBottom: '1px solid #e5e7eb',
                            }}>
                                <h3 style={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    marginBottom: '12px',
                                }}>
                                    Actions
                                </h3>
                                {loan.user_id && (
                                    <a
                                        href={`https://los-prod.dailype.in/muthoot/session-link?user_id=${loan.user_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            width: '100%',
                                            padding: '12px 16px',
                                            backgroundColor: '#1565d8',
                                            color: '#ffffff',
                                            borderRadius: '8px',
                                            textDecoration: 'none',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            transition: 'background-color 0.2s',
                                            marginBottom: '12px',
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#0d47a1')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#1565d8')}
                                    >
                                        🚀 Start Journey
                                    </a>
                                )}
                                <button
                                    className={styles.syncDataButton}
                                    onClick={handleSyncAnalysis}
                                    disabled={analysisStatus === 'syncing'}
                                    style={{ width: '100%' }}
                                >
                                    {analysisStatus === 'syncing' ? 'Syncing...' : 'Sync Data'}
                                </button>
                            </div>

                            {/* Actions Taken Timeline */}
                            <div className={styles.actionsTakenSection}>
                                <h3 className={styles.actionsTakenHeader}>
                                    Actions Taken
                                </h3>
                                {loadingActions ? (
                                    <div className={styles.actionsTakenLoading}>Loading actions...</div>
                                ) : actionsTaken.length === 0 ? (
                                    <div className={styles.actionsTakenEmpty}>No actions found</div>
                                ) : (
                                    <div className={styles.actionsDayGroupList}>
                                        {actionsTaken.map((dayGroup, idx) => (
                                            <div key={dayGroup.day_group ?? `group-${idx}`} className={styles.actionsDayGroup}>
                                                <span className={styles.actionsDayLabel}>
                                                    {formatDayGroupDate(dayGroup.events)}
                                                </span>
                                                <div className={styles.actionsEventList}>
                                                    {dayGroup.events.map((event) => (
                                                        <div
                                                            key={event.event_id}
                                                            className={`${styles.actionsEventCard} ${getEventCardClass(event.event_type)}`}
                                                            onClick={() => toggleEventExpand(event.event_id)}
                                                        >
                                                            <div className={styles.actionsEventTop}>
                                                                <span className={styles.actionsEventType}>
                                                                    <span className={styles.actionsEventTypeIcon}>
                                                                        {getEventTypeIcon(event.event_type)}
                                                                    </span>
                                                                    {getEventTypeLabel(event.event_type)}
                                                                </span>
                                                                <span className={`${styles.actionsEventStatus} ${getStatusClass(event.status)}`}>
                                                                    {event.status}
                                                                </span>
                                                            </div>
                                                            <span className={styles.actionsEventTime}>
                                                                {formatEventTime(event.execute_at)}
                                                            </span>
                                                            {(event.status === 'executed') && renderEventDetails(event)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Panel - Loan Details */}
                    <div className={styles.rightPanel}>
                        <div className={styles.tabs}>
                            <button
                                className={`${styles.tab} ${activeTab === 'overview' ? styles.activeTab : ''}`}
                                onClick={() => setActiveTab('overview')}
                            >
                                Loan Details
                            </button>
                            <button
                                className={`${styles.tab} ${activeTab === 'notes' ? styles.activeTab : ''}`}
                                onClick={() => setActiveTab('notes')}
                            >
                                Notes {notes.length > 0 && `(${notes.length})`}
                            </button>
                            {shouldShowDocumentsTab && (
                                <button
                                    className={`${styles.tab} ${activeTab === 'documents' ? styles.activeTab : ''}`}
                                    onClick={() => setActiveTab('documents')}
                                >
                                    Document Analysis {kycDocuments.length > 0 && `(${kycDocuments.length})`}
                                </button>
                            )}
                            <button
                                className={`${styles.tab} ${activeTab === 'timeline' ? styles.activeTab : ''}`}
                                onClick={() => setActiveTab('timeline')}
                            >
                                Timeline {timeline.length > 0 && `(${timeline.length})`}
                            </button>
                        </div>

                        <div className={styles.content}>
                            {activeTab === 'overview' && (
                                <div className={styles.overview}>
                                    <div className={styles.section}>
                                        <h2 className={styles.sectionTitle}>LOAN DETAILS</h2>
                                        <div className={styles.detailsGrid}>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>Loan Type:</span>
                                                <span className={styles.detailValue}>{loan.loan_type || '-'}</span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>Loan ID:</span>
                                                <span className={styles.detailValue}>{loan.loan_id}</span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>Customer ID:</span>
                                                <span
                                                    className={`${styles.detailValue} ${styles.clickableLink}`}
                                                    onClick={() => {
                                                        // Could navigate to user profile if needed
                                                        console.log('Navigate to user:', loan.user_id);
                                                    }}
                                                >
                                                    {loan.user_id}
                                                </span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>Created At:</span>
                                                <span className={styles.detailValue}>
                                                    {loan.loan_created_at
                                                        ? new Date(loan.loan_created_at).toLocaleDateString('en-GB', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        })
                                                        : '-'}
                                                </span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>Updated At:</span>
                                                <span className={styles.detailValue}>
                                                    {loan.loan_updated_at
                                                        ? new Date(loan.loan_updated_at).toLocaleDateString('en-GB', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        })
                                                        : '-'}
                                                </span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>Requested Loan Amount:</span>
                                                <span className={styles.detailValue}>
                                                    {loan.requested_amt ? `₹${loan.requested_amt.toLocaleString('en-IN')}` : '-'}
                                                </span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>Approved Loan Amount:</span>
                                                <span className={styles.detailValue}>
                                                    {loan.lender_approved_amt ? `₹${loan.lender_approved_amt.toLocaleString('en-IN')}` : '-'}
                                                </span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>Disbursed Loan Amount:</span>
                                                <span className={styles.detailValue}>
                                                    {loan.disbursed_amt ? `₹${loan.disbursed_amt.toLocaleString('en-IN')}` : '-'}
                                                </span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>Loan Disburse Date:</span>
                                                <span className={styles.detailValue}>
                                                    {loan.disbursed_date
                                                        ? new Date(loan.disbursed_date).toLocaleDateString('en-GB', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        })
                                                        : '-'}
                                                </span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>Processing Fee:</span>
                                                <span className={styles.detailValue}>
                                                    {loan.loan_processing_fees_amt
                                                        ? `₹${loan.loan_processing_fees_amt.toLocaleString('en-IN')}`
                                                        : '-'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.section}>
                                        <h2 className={styles.sectionTitle}>ADDITIONAL INFO</h2>
                                        <div className={styles.detailsGrid}>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>Agent ID:</span>
                                                <span className={styles.detailValue}>{loan.agent_id || '-'}</span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>CIBIL Score:</span>
                                                <span className={styles.detailValue}>{loan.bureau_score || '-'}</span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.detailLabel}>CIBIL Status:</span>
                                                <span
                                                    className={styles.detailValue}
                                                    style={{
                                                        color: getStatusColor(loan.bureau_name || loan.is_active?.toString()),
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {loan.is_active ? 'ACTIVE' : 'INACTIVE'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'notes' && (
                                <div className={styles.notes}>
                                    <div className={styles.addNoteSection}>
                                        <textarea
                                            className={styles.noteInput}
                                            placeholder="Add a note..."
                                            value={newNote}
                                            onChange={(e) => setNewNote(e.target.value)}
                                            rows={4}
                                        />
                                        <button
                                            className={styles.addNoteButton}
                                            onClick={handleAddNote}
                                            disabled={addingNote || !newNote.trim()}
                                        >
                                            {addingNote ? 'Adding...' : 'Add Note'}
                                        </button>
                                    </div>

                                    <div className={styles.notesList}>
                                        {loadingNotes ? (
                                            <div className={styles.skeletonContainer}>
                                                {[1, 2, 3].map((i) => (
                                                    <div key={i} className={styles.noteCard}>
                                                        <div className={styles.noteHeader}>
                                                            <Skeleton className={styles.skeletonText} style={{ width: '120px', height: '16px' }} />
                                                            <Skeleton className={styles.skeletonText} style={{ width: '150px', height: '16px' }} />
                                                        </div>
                                                        <Skeleton className={styles.skeletonText} style={{ width: '100%', height: '60px', marginTop: '8px' }} />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : notes.length === 0 ? (
                                            <div className={styles.emptyState}>No notes yet</div>
                                        ) : (
                                            notes.map((note) => (
                                                <div key={note.note_id} className={styles.noteCard}>
                                                    <div className={styles.noteHeader}>
                                                        <span className={styles.noteAuthor}>{note.createdBy}</span>
                                                        <span className={styles.noteDate}>
                                                            {new Date(note.createdAt).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <div
                                                        className={styles.noteContent}
                                                        dangerouslySetInnerHTML={{ __html: note.note }}
                                                    />
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}

                            {shouldShowDocumentsTab && activeTab === 'documents' && (
                                <div className={styles.documents}>
                                    {/* Analysis Data Section */}
                                    {analysisData && Object.keys(analysisData).length > 0 && (
                                        <div className={styles.analysisSection}>
                                            <h3 className={styles.analysisTitle}>Extracted Data</h3>
                                            <div className={styles.analysisData}>
                                                {Object.entries(analysisData).map(([key, value]) => (
                                                    <div key={key} className={styles.analysisField}>
                                                        <span className={styles.analysisKey}>
                                                            {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}:
                                                        </span>
                                                        <span className={styles.analysisValue}>
                                                            {typeof value === 'object' && value !== null
                                                                ? JSON.stringify(value, null, 2)
                                                                : String(value || 'N/A')}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Documents Section */}
                                    <div className={styles.documentsSection}>
                                        <div className={styles.sectionHeader}>
                                            <h3 className={styles.documentsTitle}>Uploaded Documents</h3>
                                            <button
                                                className={styles.syncButton}
                                                onClick={handleSyncAnalysis}
                                                disabled={analysisStatus === 'syncing'}
                                            >
                                                {analysisStatus === 'syncing'
                                                    ? 'Analyzing...'
                                                    : analysisStatus === 'success'
                                                        ? 'Re-analyze'
                                                        : 'Analyze Documents'}
                                            </button>
                                        </div>

                                        {analysisStatus === 'syncing' && (
                                            <div className={styles.analysisStatus}>
                                                <span className={styles.statusText}>Analyzing documents, please wait...</span>
                                            </div>
                                        )}

                                        {analysisStatus === 'success' && (
                                            <div className={styles.analysisStatusSuccess}>
                                                <span className={styles.statusText}>✓ Analysis completed successfully</span>
                                            </div>
                                        )}

                                        {analysisStatus === 'error' && (
                                            <div className={styles.analysisStatusError}>
                                                <span className={styles.statusText}>✗ Analysis failed. Please try again.</span>
                                            </div>
                                        )}

                                        {loadingDocuments ? (
                                            <div className={styles.documentsGrid}>
                                                {[1, 2, 3, 4].map((i) => (
                                                    <div key={i} className={styles.documentCard}>
                                                        <Skeleton className={styles.skeletonText} style={{ width: '120px', height: '20px' }} />
                                                        <Skeleton style={{ width: '100%', height: '200px', borderRadius: '4px' }} />
                                                        <Skeleton className={styles.skeletonText} style={{ width: '100%', height: '36px', borderRadius: '6px' }} />
                                                        <Skeleton className={styles.skeletonText} style={{ width: '150px', height: '14px' }} />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : kycDocuments.length === 0 ? (
                                            <div className={styles.emptyState}>No documents found</div>
                                        ) : (
                                            <div className={styles.documentsGrid}>
                                                {kycDocuments.map((doc) => (
                                                    <div key={doc.document_id} className={styles.documentCard}>
                                                        <div className={styles.documentType}>{doc.document_type}</div>
                                                        <img
                                                            src={doc.document_url}
                                                            alt={doc.document_type}
                                                            className={styles.documentImage}
                                                            onError={(e) => {
                                                                (e.target as HTMLImageElement).style.display = 'none';
                                                            }}
                                                        />
                                                        <a
                                                            href={doc.document_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={styles.documentLink}
                                                        >
                                                            View Document
                                                        </a>
                                                        <div className={styles.documentDate}>
                                                            Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'timeline' && (
                                <div className={styles.timeline}>
                                    {loadingTimeline ? (
                                        <div className={styles.timelineList}>
                                            {[1, 2, 3, 4].map((i) => (
                                                <div key={i} className={styles.timelineEvent}>
                                                    <div className={styles.timelineDot} />
                                                    <div className={styles.timelineContent}>
                                                        <div className={styles.timelineHeader}>
                                                            <Skeleton className={styles.skeletonText} style={{ width: '150px', height: '16px' }} />
                                                            <Skeleton className={styles.skeletonText} style={{ width: '180px', height: '16px' }} />
                                                        </div>
                                                        <Skeleton className={styles.skeletonText} style={{ width: '100%', height: '40px', marginTop: '8px' }} />
                                                        <div className={styles.timelineMeta} style={{ marginTop: '8px' }}>
                                                            <Skeleton className={styles.skeletonText} style={{ width: '100px', height: '14px' }} />
                                                            <Skeleton className={styles.skeletonText} style={{ width: '120px', height: '14px' }} />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : timeline.length === 0 ? (
                                        <div className={styles.emptyState}>No timeline events found</div>
                                    ) : (
                                        <div className={styles.timelineList}>
                                            {timeline.map((event, index) => (
                                                <div key={index} className={styles.timelineEvent}>
                                                    <div className={styles.timelineDot} />
                                                    <div className={styles.timelineContent}>
                                                        <div className={styles.timelineHeader}>
                                                            <span className={styles.timelineEventType}>{event.eventType}</span>
                                                            <span className={styles.timelineDate}>
                                                                {new Date(event.loggedAt).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <div className={styles.timelineDescription}>{event.eventDescription}</div>
                                                        <div className={styles.timelineMeta}>
                                                            <span>Entity: {event.entityType}</span>
                                                            <span>Source: {event.sourceEntityID}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

